import Axios from 'axios';
import qs from 'qs';
import { LOCAL_STORAGE_KEY } from '@/helpers/enums';
import { useAuthStore } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { ToastService } from '@/composables/useToastService';

// HTTP status codes
const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  SUCCESS: 200,
  CREATED: 201,
  NO_CONTENT: 204,
};

// Application response codes
const APP_CODES = {
  SUCCESS: 0,
  UNAUTHORIZED: 401,
};

// Request timeout in milliseconds
const REQUEST_TIMEOUT = 30000;

/**
 * Merge query parameters into axios config
 */
function mergeAxiosConfig(data, config = {}) {
  return {
    ...config,
    params: data,
    paramsSerializer: {
      serialize: (params) =>
        qs.stringify(params, {
          arrayFormat: 'repeat',
          skipNulls: true,
          allowDots: true,
        }),
    },
  };
}

/**
 * Enhanced RestClient with better error handling and modern practices
 */
export default class RestClient {
  #client;
  #version;
  #servicePath;
  #baseURL;

  constructor(servicePath, version = 'v1', baseURL = import.meta.env.VITE_API_URL) {
    if (!servicePath) {
      throw new Error('servicePath is required');
    }
    this.#version = version;
    const normalizedPath = servicePath.startsWith('/') ? servicePath : `/${servicePath}`;
    this.#servicePath = normalizedPath === '/' ? '/app' : `/app${normalizedPath}`;
    this.#baseURL = baseURL ? baseURL.replace(/\/+$/, '') : '';
    this.#client = this.createAxiosClient();
  }

  createAxiosClient() {
    if (!this.#baseURL) {
      throw new Error('API_URL environment variable is not set');
    }

    const axiosInstance = Axios.create({
      baseURL: this.#baseURL,
      timeout: REQUEST_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      withCredentials: false,
    });

    // Request interceptor
    axiosInstance.interceptors.request.use(
      this.onRequest.bind(this),
      this.onRequestError.bind(this),
    );

    // Response interceptor
    axiosInstance.interceptors.response.use(
      this.onResponse.bind(this),
      this.onResponseError.bind(this),
    );

    return axiosInstance;
  }

  async onRequest(config) {
    try {
      // Extend token validity if needed
      await this.extendTokenValidityPeriod();

      // Get auth token
      const token = this.getAuthToken();

      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Log request in development
      // if (import.meta.env.DEV) {
      //   console.log(`[API Request] ${config.method?.toUpperCase()} ${config.url}`, {
      //     data: config.data,
      //     params: config.params,
      //   });
      // }

      return config;
    } catch (error) {
      console.error('Request interceptor error:', error);
      return Promise.reject(error);
    }
  }

  onRequestError(error) {
    console.error('Request setup error:', error);
    return Promise.reject(error);
  }

  onResponse(response) {
    // Log response in development
    // if (import.meta.env.DEV) {
    //   console.log(
    //     `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url}`,
    //     {
    //       status: response.status,
    //       data: response.data,
    //     },
    //   );
    // }

    return response;
  }

  onResponseError(error) {
    if (import.meta.env.DEV) {
      console.error('[API Error]', error);
    }
    return Promise.reject(error);
  }

  /**
   * Get authentication token from store or localStorage
   */
  getAuthToken() {
    try {
      const auth = useAuthStore();
      return auth.token || localStorage.getItem(LOCAL_STORAGE_KEY.TOKEN);
    } catch (error) {
      console.warn('Failed to get auth token:', error);
      return localStorage.getItem(LOCAL_STORAGE_KEY.TOKEN);
    }
  }

  /**
   * Extend token validity period
   * Override this method in subclasses if needed
   */
  async extendTokenValidityPeriod() {
    // Implement token refresh logic here if needed
    return Promise.resolve();
  }

  /**
   * Handle application-level errors
   */
  handleApplicationError(result) {
    if (result.code === APP_CODES.UNAUTHORIZED) {
      try {
        const auth = useAuthStore();
        auth.signOut();
        ToastService.error('Session expired. Please log in again.');
      } catch (error) {
        console.error('Failed to sign out:', error);
      }
      return;
    }
    if (result.code !== APP_CODES.SUCCESS && result.message) {
      ToastService.error(result.message);
    }
  }

  /**
   * Handle HTTP errors
   */
  handleHttpError(err) {
    if (!Axios.isAxiosError(err)) {
      // ToastService.error(err.message || 'An unexpected error occurred');
      return;
    }

    if (err.response) {
      // Server responded with error status
      const { status, data } = err.response;
      if (data?.message) {
        ToastService.error(data.message);
      } else {
        switch (status) {
          case HTTP_STATUS.UNAUTHORIZED:
            ToastService.error('Unauthorized access');
            break;
          case 403:
            ToastService.error('Access forbidden');
            break;
          case 404:
            ToastService.error('Resource not found');
            break;
          case 500:
            ToastService.error('Internal server error');
            break;
          default:
            ToastService.error(`Request failed with status ${status}`);
        }
      }
    } else if (err.request) {
      // Network error
      if (err.code === 'ECONNABORTED') {
        ToastService.error('Request timeout. Please try again.');
      } else {
        ToastService.error('Network error. Please check your connection.');
      }
    } else {
      ToastService.exception(err);
    }
  }

  /**
   * Build complete API URL
   */
  buildUrl(endpoint) {
    const cleanEndpoint = endpoint ? endpoint.replace(/^\/+/, '') : '';
    return `api/${this.#version}${this.#servicePath}/${cleanEndpoint}`;
  }

  /**
   * Generic request method with improved error handling
   */
  async request(method, url, data, config = {}) {
    const app = useAppStore();
    const { silent = false, noLoading = false, ...restConfig } = config;

    try {
      if (!noLoading) {
        // Increment loading counter
        app.increaseLoadingCount();
      }

      const axiosConfig = {
        ...restConfig,
        url: this.buildUrl(url),
        method: method.toUpperCase(),
        data,
      };

      const response = await this.#client.request(axiosConfig);
      const result = response.data;

      // Handle application-level errors
      if (result.code !== APP_CODES.SUCCESS) {
        if (!silent) {
          this.handleApplicationError(result);
        }
        throw new Error(result.message || 'Application error');
      }

      return result;
    } catch (error) {
      if (!silent) {
        this.handleHttpError(error, true);
      }
      throw error;
    } finally {
      // Always decrement loading counter
      setTimeout(() => app.decreaseLoadingCount(), 500);
    }
  }

  /**
   * GET request
   */
  async get(url, params, config = {}) {
    const axiosConfig = mergeAxiosConfig(params, config);
    return this.request('GET', url, null, axiosConfig);
  }

  /**
   * POST request
   */
  async post(url, data, config = {}) {
    return this.request('POST', url, data, config);
  }

  /**
   * PUT request
   */
  async put(url, data, config = {}) {
    return this.request('PUT', url, data, config);
  }

  /**
   * PATCH request
   */
  async patch(url, data, config = {}) {
    return this.request('PATCH', url, data, config);
  }

  /**
   * DELETE request
   */
  async delete(url, data, config = {}) {
    return this.request('DELETE', url, data, config);
  }

  /**
   * Upload file with progress tracking
   */
  async upload(url, formData, onUploadProgress, config = {}) {
    const uploadConfig = {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config.headers,
      },
      onUploadProgress,
    };

    return this.request('POST', url, formData, uploadConfig);
  }

  /**
   * Download file
   */
  async download(url, params, config = {}) {
    const downloadConfig = {
      ...mergeAxiosConfig(params, config),
      responseType: 'blob',
    };

    return this.request('GET', url, null, downloadConfig);
  }

  /**
   * Cancel all pending requests
   */
  cancelAllRequests() {
    // Implementation depends on your cancellation strategy
    // You might want to keep track of active requests and cancel them
    console.warn('Request cancellation not implemented');
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return {
      baseURL: this.#baseURL,
      version: this.#version,
      servicePath: this.#servicePath,
    };
  }
}
